// Filling letter templates and printing them as bilingual pages.

const PLACEHOLDER = /\{([a-z_]+)\}/g;

/** Placeholders a template uses, in order of first appearance. */
export function placeholders(...texts: string[]): string[] {
  const seen = new Set<string>();
  for (const t of texts) for (const m of t.matchAll(PLACEHOLDER)) seen.add(m[1]);
  return [...seen];
}

export function fill(text: string, values: Record<string, string | undefined>): string {
  return text.replace(PLACEHOLDER, (all, key: string) => (values[key] ? values[key]! : all));
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const paras = (s: string) => esc(s).split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");

/** Opens a print-ready page in a new window: letterhead, reference and date, English left and Arabic right. */
export function printLetter(o: {
  company: string; companyAr: string; reference: string; date: string; title: string; en: string; ar: string;
  signatoryName?: string; signatoryTitle?: string;
}, opened?: Window | null) {
  const w = opened ?? window.open("", "_blank");
  if (!w) return false;
  const sign = (o.signatoryName || o.signatoryTitle)
    ? `<div class="sign"><div class="line"></div><strong>${esc(o.signatoryName ?? "")}</strong><br>${esc(o.signatoryTitle ?? "")}</div>` : `<div class="sign"><div class="line"></div></div>`;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(o.reference)} — ${esc(o.title)}</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600&family=IBM+Plex+Sans+Arabic:wght@400;600&display=swap">
<style>
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: "IBM Plex Sans", "IBM Plex Sans Arabic", Arial, sans-serif; color: #14262c; font-size: 11.5pt; line-height: 1.55; margin: 0; }
  header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #0f4c5c; padding-bottom: 10px; margin-bottom: 18px; }
  header img { height: 34px; }
  .co { text-align: end; font-weight: 600; color: #0f4c5c; }
  .meta { display: flex; justify-content: space-between; font-size: 10pt; color: #556a71; margin-bottom: 18px; }
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
  .ar { direction: rtl; text-align: right; font-family: "IBM Plex Sans Arabic", Arial, sans-serif; font-size: 12.5pt; line-height: 1.8; }
  h1 { font-size: 13pt; margin: 0 0 12px; color: #0f4c5c; }
  p { margin: 0 0 10px; }
  .sign { margin-top: 36px; font-size: 10.5pt; }
  .sign .line { width: 180px; border-bottom: 1px solid #14262c; height: 40px; margin-bottom: 6px; }
  .ar .sign .line { margin-inline-start: 0; }
  footer { margin-top: 40px; font-size: 9pt; color: #556a71; border-top: 1px solid #d5dee2; padding-top: 8px; }
  @media print { .noprint { display: none; } }
  .noprint { position: fixed; top: 10px; inset-inline-end: 10px; padding: 8px 14px; background: #0f4c5c; color: #fff; border: 0; border-radius: 8px; font: inherit; cursor: pointer; }
</style></head><body>
<button class="noprint" onclick="window.print()">Print / Save as PDF</button>
<header><img src="${location.origin}/peoplecore-logo.svg" alt=""><div class="co">${esc(o.company)}<br><span style="font-family:'IBM Plex Sans Arabic'">${esc(o.companyAr)}</span></div></header>
<div class="meta"><span>Ref: ${esc(o.reference)}</span><span>${esc(o.date)}</span></div>
<div class="cols">
  <section><h1>${esc(o.title)}</h1>${paras(o.en)}${sign}</section>
  <section class="ar" lang="ar">${o.ar.trim() ? paras(o.ar) + sign : ""}</section>
</div>
<footer>${esc(o.company)} · ${esc(o.reference)}</footer>
<script>window.onload = () => setTimeout(() => window.print(), 400);</script>
</body></html>`);
  w.document.close();
  return true;
}
